import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import Select from '@atlaskit/select/Select';
import { useInProductHelp } from '@atlassian/proforma-common-core/jira-common-context';
import { useAttachmentService } from '../../../services/attachments';
import { useLastSelectedFormService } from '../../../services/last-selected-form';
import { useProjectFormsService } from '../../../services/project-forms';
import { useProjectsService } from '../../../services/projects';
import { messages } from './messages';
import { IssueFormsHeader, IssueFormsHeaderWrapper, IssueFormsPickers, SectionMessageWrapper } from './styled';
import { formatLabel, formOption, projectOption, renderOption, renderSingleValue, selectStyles, sortSelectOption, typeOption } from './utils';
export const Header = ({
  setFormDetails
}) => {
  const {
    onOpenInProductHelpArticle
  } = useInProductHelp();
  const intl = useIntl();
  const [{
    data: lastSelectedFormDetails,
    loading: loadingLastSelectedFormDetails
  }, {
    set: setLastSelectedFormDetails
  }] = useLastSelectedFormService();
  const [, {
    loadMediaApiUploadToken
  }] = useAttachmentService();
  const [{
    data: projectForms,
    error: projectFormsError,
    loading: projectFormsLoading
  }, {
    load: loadProjectForms
  }] = useProjectFormsService();
  const [{
    data: projectsDetails,
    error: projectsError,
    loading: projectsLoading
  }] = useProjectsService();
  const [selectedProject, setSelectedProject] = useState();
  const [selectedType, setSelectedType] = useState();
  const [selectedForm, setSelectedForm] = useState();
  const projects = useMemo(() => {
    if (projectsDetails === undefined) {
      return undefined;
    }
    return projectsDetails.map(projectOption).sort(sortSelectOption);
  }, [projectsDetails]);
  const types = useMemo(() => {
    if (selectedProject === undefined || projectForms === undefined) {
      return undefined;
    }
    const typeOptions = [];
    projectForms.forEach(projectForm => {
      if (!typeOptions.some(type => {
        var _projectForm$requestT;
        return type.issueTypeId === projectForm.issueType.id && type.requestTypeId === ((_projectForm$requestT = projectForm.requestType) === null || _projectForm$requestT === void 0 ? void 0 : _projectForm$requestT.id);
      })) {
        typeOptions.push(typeOption(projectForm));
      }
    });
    return typeOptions.sort(sortSelectOption);
  }, [projectForms, selectedProject]);
  const forms = useMemo(() => {
    if (selectedType === undefined || projectForms === undefined) {
      return undefined;
    }
    const formOptions = [];
    projectForms.forEach(projectForm => {
      var _projectForm$requestT2;
      if (selectedType.issueTypeId === projectForm.issueType.id && selectedType.requestTypeId === ((_projectForm$requestT2 = projectForm.requestType) === null || _projectForm$requestT2 === void 0 ? void 0 : _projectForm$requestT2.id) && !formOptions.some(form => form.value === projectForm.projectFormId)) {
        formOptions.push(formOption(projectForm));
      }
    });
    if (formOptions.length === 1) {
      setSelectedForm(formOptions[0]);
    }
    return formOptions.sort(sortSelectOption);
  }, [projectForms, selectedType]);
  useEffect(() => {
    setFormDetails(undefined);
    if (projectForms && selectedForm && selectedType && selectedProject) {
      const formDetails = projectForms.find(projectForm => {
        var _projectForm$requestT3;
        return projectForm.projectId.toString() === selectedProject.value && projectForm.issueType.id === selectedType.issueTypeId && ((_projectForm$requestT3 = projectForm.requestType) === null || _projectForm$requestT3 === void 0 ? void 0 : _projectForm$requestT3.id) === selectedType.requestTypeId && projectForm.projectFormId === selectedForm.value;
      });
      if (formDetails) {
        setFormDetails(formDetails);
      }
      const project = projectsDetails === null || projectsDetails === void 0 ? void 0 : projectsDetails.find(projectDetails => projectDetails.id === selectedProject.value);
      if (project && formDetails) {
        setLastSelectedFormDetails({
          project,
          formDetails
        });
      }
    }
  }, [projectForms, projectsDetails, selectedForm, selectedProject, selectedType, setFormDetails, setLastSelectedFormDetails]);
  useEffect(() => {
    if (!loadingLastSelectedFormDetails && lastSelectedFormDetails) {
      loadProjectForms(lastSelectedFormDetails.project.id);
      setSelectedProject(projectOption(lastSelectedFormDetails.project));
      setSelectedType(typeOption(lastSelectedFormDetails.formDetails));
      setSelectedForm(formOption(lastSelectedFormDetails.formDetails));
    }
    // NOTE: If `lastSelectedFormDetails` is included, then this is triggered everytime a form is selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadProjectForms, loadingLastSelectedFormDetails, setFormDetails]);
  const openFormHelp = () => onOpenInProductHelpArticle('5TZElbp1tEXG70CGT0HmT4');
  let projectHasNoAssociatedFormsMessage;
  if ((selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.projectType) === 'SERVICE_DESK') {
    projectHasNoAssociatedFormsMessage = messages.projectHasNoAssociatedFormsJsm;
  } else if ((selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.projectType) === 'SOFTWARE') {
    projectHasNoAssociatedFormsMessage = messages.projectHasNoAssociatedFormsJsw;
  } else {
    projectHasNoAssociatedFormsMessage = messages.projectHasNoAssociatedFormsJwm;
  }
  return /*#__PURE__*/React.createElement(IssueFormsHeaderWrapper, null, /*#__PURE__*/React.createElement(IssueFormsHeader, null, /*#__PURE__*/React.createElement("h2", null, /*#__PURE__*/React.createElement(FormattedMessage, messages.pageHeader)), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.pageDescriptionEmbed, {
    values: {
      a: label => /*#__PURE__*/React.createElement(Button, {
        appearance: "link",
        spacing: "none",
        onClick: openFormHelp
      }, label)
    }
  }))), /*#__PURE__*/React.createElement(IssueFormsPickers, null, /*#__PURE__*/React.createElement(Select, {
    components: {
      Option: renderOption(),
      SingleValue: renderSingleValue()
    },
    controlShouldRenderValue: false,
    id: "proforma-jira-issue-forms.ui.issue-forms.header.project-select" // Should be `testId`.
    ,
    inputId: "proforma-jira-issue-forms.ui.issue-forms.header.project-select-input",
    instanceId: "proforma-jira-issue-forms.ui.issue-forms.header.project-select",
    isDisabled: projects === undefined || projects.length < 1,
    isLoading: projectsLoading,
    onChange: project => {
      setSelectedProject(project !== null && project !== void 0 ? project : undefined);
      setSelectedType(undefined);
      setSelectedForm(undefined);
      if (project) {
        loadProjectForms(project.value);
        loadMediaApiUploadToken(project.value);
      }
    },
    options: projects,
    placeholder: formatLabel(intl.formatMessage(messages.projectPlaceholder), selectedProject)
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    ,
    styles: selectStyles(),
    value: selectedProject
  }), /*#__PURE__*/React.createElement(Select, {
    components: {
      Option: renderOption(),
      SingleValue: renderSingleValue()
    },
    controlShouldRenderValue: false,
    id: "proforma-jira-issue-forms.ui.issue-forms.header.type-select" // Should be `testId`.
    ,
    inputId: "proforma-jira-issue-forms.ui.issue-forms.header.type-select-input",
    instanceId: "proforma-jira-issue-forms.ui.issue-forms.header.type-select",
    isDisabled: selectedProject === undefined || types === undefined || types.length < 1,
    isLoading: projectFormsLoading,
    onChange: type => {
      setSelectedType(type !== null && type !== void 0 ? type : undefined);
      setSelectedForm(undefined);
    },
    options: types,
    placeholder: formatLabel(intl.formatMessage((selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.projectType) === 'SERVICE_DESK' ? messages.requestTypePlaceholder : messages.issueTypePlaceholder), selectedType)
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    ,
    styles: selectStyles(),
    value: selectedType !== null && selectedType !== void 0 ? selectedType : null
  }), /*#__PURE__*/React.createElement(Select, {
    controlShouldRenderValue: false,
    id: "proforma-jira-issue-forms.ui.issue-forms.header.form-select" // Should be `testId`.
    ,
    inputId: "proforma-jira-issue-forms.ui.issue-forms.header.form-select-input",
    instanceId: "proforma-jira-issue-forms.ui.issue-forms.header.form-select",
    isDisabled: selectedType === undefined || forms === undefined || forms.length < 1,
    isLoading: projectFormsLoading,
    onChange: form => setSelectedForm(form !== null && form !== void 0 ? form : undefined),
    options: forms,
    placeholder: formatLabel(intl.formatMessage(messages.formPlaceholder), selectedForm)
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    ,
    styles: selectStyles(),
    value: selectedForm !== null && selectedForm !== void 0 ? selectedForm : null
  })), projectsError && /*#__PURE__*/React.createElement(SectionMessageWrapper, {
    "data-testid": "proforma-jira-issue-forms.ui.issue-forms.header.projects-error"
  }, /*#__PURE__*/React.createElement(SectionMessage, {
    appearance: "error",
    title: intl.formatMessage(messages.errorLoadingProjectsTitle)
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.errorLoadingProjectsDescription))), projectFormsError && /*#__PURE__*/React.createElement(SectionMessageWrapper, {
    "data-testid": "proforma-jira-issue-forms.ui.issue-forms.header.project-forms-error"
  }, /*#__PURE__*/React.createElement(SectionMessage, {
    appearance: "error",
    title: intl.formatMessage(messages.errorLoadingProjectAssociatedFormsTitle)
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.errorLoadingProjectAssociatedFormsDescription))), types !== undefined && types.length < 1 && /*#__PURE__*/React.createElement(SectionMessageWrapper, {
    "data-testid": "proforma-jira-issue-forms.ui.issue-forms.header.no-project-forms"
  }, /*#__PURE__*/React.createElement(SectionMessage, {
    appearance: "warning"
  }, /*#__PURE__*/React.createElement(FormattedMessage, projectHasNoAssociatedFormsMessage)))));
};